import React, { useContext, useMemo, useCallback, useRef, useState, Fragment, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay as DndKitDragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { ACTIVITY_TYPE, CATEGORY_TYPE, PERIOD_DAYS, PERIOD_MONTHS, PERIOD_QUARTERS, PERIOD_YEARS, SUBCATEGORY_TYPE } from 'Constants/project_plan';
import { MultiGrid, AutoSizer, defaultCellRangeRenderer } from 'react-virtualized';

import { startConflictMessage } from '../dependencies/earliestStart';
import ProjectPlanEmptyState, { VARIANT_EMPTY, VARIANT_FILTERED, VARIANT_SEARCHED, VARIANT_SEARCHED_FILTERED } from '../ProjectPlanEmptyState';
import {
    SCHEDULE_COMPLETE,
    SCHEDULE_NO_LINKS,
    SCHEDULE_NOT_LINKED_TO_TARGET,
    SCHEDULE_SCHEDULED,
    floatLabel,
    isCriticalEntry,
    scheduleEntries,
} from '../schedule/scheduleEntries';

import { buildActivityRowIndex, computeArrows, createTimelineScale } from './dependencyArrowGeometry';
import DependencyArrows from './DependencyArrows';
import GanttChartSkeleton from './GanttChartSkeleton';
import OwnerCell from './OwnerCell';

import { formatDateLong } from 'Helpers/dateFormat';
import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import CircularProgress from 'CommonComponents/CircularProgress';
import Tooltip from 'CommonComponents/Tooltip';

import ProjectPeriodContext from 'Context/project_plan/ProjectPeriodContext';
import ProjectPlanContext, { FILTER_MAP_KEYS } from 'Context/project_plan/ProjectPlanContext';

import useDropdown from 'Hooks/useDropdown';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import DiamondIcon from 'Assets/icons/diamond.svg';
import GripIcon from 'Assets/icons/grip.svg';
import PenSimpleIcon from 'Assets/icons/pen-simple.svg';
import SortIcon from 'Assets/icons/sort.svg';
import TriangleDownIcon from 'Assets/icons/triangle-down-filled.svg';
import UsersIcon from 'Assets/icons/users.svg';
import WarningTriangleIcon from 'Assets/icons/warning-triangle.svg';

const YEARS_COLUMN_WIDTH = 500;
const QUARTERS_COLUMN_WIDTH = 300;
const MONTHS_COLUMN_WIDTH = 128;
const DAYS_COLUMN_WIDTH = 128;

// Pinned column widths, measured against the longest real values in the data. They were sized at
// Outfit 14px inside a 2px-bordered cell; the wireframe pass since dropped the text to 13px and
// the interior borders to none, so every column now has more room than it was measured with, not
// less. Each budget was width − border − 12px horizontal cell padding.
const NAME_COLUMN_WIDTH = 460; // the category / sub-category / activity tree, incl. its indent, grip and row actions
const OWNER_COLUMN_WIDTH = 72; // fits the 26px initials chip, with room for the "Owner" heading, which is what sets it
const SOURCE_COLUMN_WIDTH = 196; // a 28-char source name (179px) plus padding and slack
// 84 rather than the 64 the heading alone needs. This was sized when the last pinned column
// carried a 20px scrollbar-compensation border that `box-sizing: border-box` took out of its
// content width; that border is gone with the rest of the column rules, so the extra is now
// simply slack.
const PEOPLE_COLUMN_WIDTH = 84;
const COST_COLUMN_WIDTH = 128;
const FLOAT_COLUMN_WIDTH = 140; // "Not linked to target" plus padding

// The pinned columns, in the order they are drawn. `name` is structural; the rest are switched
// on and off by the toolbar's Columns panel, and a hidden one is absent from the grid entirely
// rather than left behind as a stub — so every index below is derived from this list, never
// hard-coded.
const FIXED_COLUMN_WIDTHS = {
    name: NAME_COLUMN_WIDTH,
    owner: OWNER_COLUMN_WIDTH,
    source: SOURCE_COLUMN_WIDTH,
    people: PEOPLE_COLUMN_WIDTH,
    cost: COST_COLUMN_WIDTH,
    float: FLOAT_COLUMN_WIDTH,
};

// Sorting by float puts the activities without one last, in this order.
const FLOAT_SORT_RANK = {
    [SCHEDULE_SCHEDULED]: 0,
    [SCHEDULE_NOT_LINKED_TO_TARGET]: 1,
    [SCHEDULE_NO_LINKS]: 2,
    [SCHEDULE_COMPLETE]: 3,
};
const FLOAT_SORT_RANK_UNKNOWN = 4;

// How an activity reads while the Critical path switch is on: a critical activity's bar or diamond
// is red; every other activity, near-critical included, has its bar or diamond faded and its name
// greyed. The rest of the row and category rows are untouched.
const TONE_CRITICAL = 'critical';
const TONE_DIMMED = 'dimmed';

/** `nearCriticalMax` is the schedule payload's `near_critical_max`: near-critical is float 1 to it. */
const isNearCriticalFloat = (float, nearCriticalMax) => float >= 1 && float <= nearCriticalMax;

const activityTone = (entry) => (isCriticalEntry(entry) ? TONE_CRITICAL : TONE_DIMMED);

/**
 * Lowest float first, critical work in path order (earliest early start), then the row order the
 * plan already had. Activities without a float follow, by state.
 */
const compareByFloat = (entries) => (a, b) => {
    const entryA = entries.get(a.item.data.id);
    const entryB = entries.get(b.item.data.id);
    const rankA = FLOAT_SORT_RANK[entryA?.state] ?? FLOAT_SORT_RANK_UNKNOWN;
    const rankB = FLOAT_SORT_RANK[entryB?.state] ?? FLOAT_SORT_RANK_UNKNOWN;

    if (rankA !== rankB) {
        return rankA - rankB;
    }

    if (entryA?.state === SCHEDULE_SCHEDULED && entryB?.state === SCHEDULE_SCHEDULED) {
        if (entryA.float !== entryB.float) {
            return entryA.float - entryB.float;
        }

        if (entryA.earlyStart !== entryB.earlyStart) {
            return (entryA.earlyStart || '') < (entryB.earlyStart || '') ? -1 : 1;
        }
    }

    return a.index - b.index;
};

/** A group's items with its activities sorted by float into the slots activities held; sub-categories stay put. */
const sortActivitiesByFloat = (items, entries) => {
    const activities = items.map((item, index) => ({ item, index })).filter(({ item }) => item.data.type === ACTIVITY_TYPE);

    if (activities.length < 2) {
        return items;
    }

    const sorted = [...activities].sort(compareByFloat(entries));
    let next = 0;

    return items.map((item) => (item.data.type === ACTIVITY_TYPE ? sorted[next++].item : item));
};

// 35px is the wireframe's row: 9px of padding above and below a 13px line. The grid draws rows
// at a fixed height rather than from padding, so the measurement is transferred directly.
const ROW_HEIGHT = 35;

// The wireframe's table surfaces, taken from it rather than from the design system's tokens,
// which are a shade or two off each one.
const HEADER_SURFACE = 'tw-bg-[#f5f7fa]'; // th
const CATEGORY_SURFACE = 'tw-bg-[#eef1f5]'; // .lvl-cat td — the same tone as a source band
const SUBCATEGORY_SURFACE = 'tw-bg-[#f6f7f9]'; // .lvl-sub td
const GRID_LINE = 'tw-border-[#e3e6ea]'; // td border-top / .card border

// Columns to pre-fetch beyond the visible range on each edge, so scrolling
// reaches already-loaded columns before hitting blanks (IB-814).
const SCROLL_BUFFER_COLUMNS = 40;

// Hard ceiling on how many columns a single lazy fetch may span. Bounds every
// request (and the overlay) to the window width rather than the plan duration,
// even on a far scrollbar jump across a multi-year Days plan (IB-814, AC#2).
const MAX_FETCH_COLUMNS = 400;

// Handle on MultiGrid's bottom-right (the only horizontally scrollable) grid. MultiGrid gives its
// four inner grids no distinguishing class of their own, so it is passed in as
// `classNameBottomRightGrid` — the supported way to address one of them from outside.
const SCROLLABLE_GRID_CLASS = 'project-gantt__scroll-grid';

const COLUMN_WIDTHS_MAP = {
    [PERIOD_DAYS]: DAYS_COLUMN_WIDTH,
    [PERIOD_MONTHS]: MONTHS_COLUMN_WIDTH,
    [PERIOD_QUARTERS]: QUARTERS_COLUMN_WIDTH,
    [PERIOD_YEARS]: YEARS_COLUMN_WIDTH,
};

// Utility function to escape HTML special characters
function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/`/g, '&#96;');
}

/**
 * The name column's heading names the tiers it actually contains, which differ by view: under a
 * source band the tree below it is source → category, and on its own it runs the full depth.
 */
const fixedColumnHeading = (columnKey, bySource) => {
    switch (columnKey) {
        case 'name':
            return bySource ? $t('Source / category') : $t('Category / sub-category / activity');
        case 'owner':
            return $t('Owner');
        case 'source':
            return $t('Source');
        case 'people':
            return $t('People');
        case 'cost':
            return $t('Cost');
        case 'float':
            return $t('Float');
        default:
            return '';
    }
};

// A by-source grouping band (IB-793). Not a row type the API knows about — these rows are
// synthesised on the client from the `source_key` every tree row already carries, so the
// band keys are namespaced to keep them clear of the `category_*` keys in `expandedRows`.
const SOURCE_BAND_TYPE = 'source-band';

const sourceBandKey = (sourceKey) => `source_band_${sourceKey}`;

const sourceBandKeyOf = (data) => sourceBandKey(data.source_key || 'master');

/** Every activity beneath a node, at any depth (a category's own activities plus its sub-categories'). */
const countActivities = (items) => (items || []).reduce((total, item) => total + (item.data.type === ACTIVITY_TYPE ? 1 : 0) + countActivities(item.items), 0);

const getTaggedPositionsTooltipContent = (taggedPositions) => {
    if (!taggedPositions || taggedPositions.length === 0) {
        return $t('No people assigned');
    }

    return `<div class="tw-flex tw-flex-col tw-space-y-0.5 tw-text-charcoal tw-text-sm tw-font-semibold">${taggedPositions.map((position) => `<div>${escapeHtml(position)}</div>`).join('')}</div>`;
};

/**
 * `columns` is the visibility map owned by the toolbar's Columns panel — `{ owner, source, people, cost, float }`.
 * `bySource` groups the top-level categories under a band per source (IB-793).
 * `criticalPath` turns on the critical path highlighting. `criticalActivityIds` is the set of
 * float-0 activity ids in the plan, or null while the Critical path switch is off. A dependency
 * arrow is red when both of its ends are in the set.
 */
const DraggableGanttChart = ({
    columns,
    bySource,
    criticalPath = false,
    criticalActivityIds = null,
    onCategoryEdit,
    onSubcategoryCreate,
    onActivityActionsShow,
    onActivityEdit,
    onActivityCreate,
    // Only the empty state uses these two: the way into a plan that has nothing in it yet.
    // `onCategoryCreate` is deliberately separate from `onSubcategoryCreate` — that one happens to
    // accept a null parent, but it is named for adding *within* a category and reusing it here
    // would read as a sub-category being created.
    onCategoryCreate,
    importProjectPlanUrl,
    // `{ links, bars }` from the links endpoint while the Links switch is on, otherwise null.
    links = null,
}) => {
    const [activeData, setActiveData] = useState(null);

    const { period, headers, columnsKeys, columnDateLookup, getDateRangeForColumnIndexes, getColumnIndexForDate } = useContext(ProjectPeriodContext);
    const {
        ganttChart,
        expandedRows,
        moveItems,
        toggleRow,
        deletableRows,
        toggleDeletableRow,
        deletable,
        disabledDeletionRows,
        isMaster,
        columnsByKey,
        line,
        anchorDate,
        loadColumnWindow,
        setRowExpanded,
        changeFilters,
        canManage,
        schedule,
        sortByFloat,
        toggleSortByFloat,
    } = useContext(ProjectPlanContext);

    const scheduleById = useMemo(() => scheduleEntries(schedule), [schedule]);

    /**
     * What the empty state says, from what the displayed response was narrowed by. Never from the
     * filter panel's draft, and never from filters whose refetch has not started yet.
     */
    const emptyVariant = useMemo(() => {
        const { filtered, search } = ganttChart.data?.requested || {};

        if (search && filtered) {
            return VARIANT_SEARCHED_FILTERED;
        }

        if (search) {
            return VARIANT_SEARCHED;
        }

        return filtered ? VARIANT_FILTERED : VARIANT_EMPTY;
    }, [ganttChart.data]);

    // Drops the maps the filter panel drives while leaving `search` alone — the two narrowings are
    // cleared by their own buttons, and wiping the search here would clear something the user was
    // not told about.
    const clearFilters = useCallback(() => changeFilters(Object.fromEntries(FILTER_MAP_KEYS.map((key) => [key, {}]))), [changeFilters]);

    const clearSearch = useCallback(() => changeFilters({ search: undefined }), [changeFilters]);

    const containerRef = useRef();
    const multiGridRef = useRef();
    const scrollDebounceRef = useRef(null);

    // Setup sensors for drag and drop
    const sensors = useSensors(useSensor(PointerSensor));

    // Sorting by float reorders activities within their own group only, so the categories and
    // sub-categories keep their order.
    const getFlatRows = useCallback(
        (items, level = 0) => {
            const ordered = sortByFloat && level > 0 ? sortActivitiesByFloat(items, scheduleById) : items;

            return ordered.flatMap(({ data, items, columns }) => {
                const row = { expandable: !!items?.length, data: { ...data, level }, columns };
                if (!expandedRows[data.key] || !items?.length) {
                    return row;
                }

                return [row, ...getFlatRows(items, level + 1)];
            });
        },
        [expandedRows, sortByFloat, scheduleById],
    );

    /**
     * The category, sub-category and source band keys with a critical activity anywhere beneath
     * them, for the marker a collapsed one shows. Keyed off the payload and the schedule, not the
     * expansion, for the same reason as `sourceBands` below.
     */
    const criticalGroups = useMemo(() => {
        const keys = new Set();

        if (!ganttChart.data?.rows || !scheduleById.size) {
            return keys;
        }

        const visit = (item) => {
            if (item.data.type === ACTIVITY_TYPE) {
                return isCriticalEntry(scheduleById.get(item.data.id));
            }

            // Every child is visited, so each nested group gets its own answer.
            const hasCritical = (item.items || []).map(visit).some(Boolean);

            if (hasCritical) {
                keys.add(item.data.key);
            }

            return hasCritical;
        };

        ganttChart.data.rows.forEach((category) => {
            if (visit(category)) {
                keys.add(sourceBandKey(category.data.source_key || 'master'));
            }
        });

        return keys;
    }, [ganttChart.data, scheduleById]);

    /**
     * The categories grouped by source, with the band row each group will be introduced by.
     *
     * Bands appear in the order their first category does, and categories keep their order
     * within a band. The backend already returns a source's categories together, so in
     * practice this groups rather than reorders; if two sources were ever interleaved, this
     * would gather each one under its band, which is the point of the view.
     *
     * Keyed off the payload alone, deliberately: the activity totals mean a full walk of a
     * tree that runs to ~15k rows, and they cannot change when a row is expanded. Folding
     * this into the expansion pass below would repeat that walk on every chevron click, on
     * top of the one `getFlatRows` already does (IB-796).
     */
    const sourceBands = useMemo(() => {
        if (!ganttChart.data?.rows) {
            return [];
        }

        const bands = new Map();

        ganttChart.data.rows.forEach((item) => {
            const key = item.data.source_key || 'master';

            if (!bands.has(key)) {
                bands.set(key, { key: sourceBandKeyOf(item.data), name: item.data.source_name, categories: [] });
            }

            bands.get(key).categories.push(item);
        });

        return Array.from(bands.values()).map((band) => ({
            categories: band.categories,
            row: {
                expandable: true,
                isBand: true,
                data: {
                    key: band.key,
                    type: SOURCE_BAND_TYPE,
                    level: 0,
                    description: band.name,
                    categories_count: band.categories.length,
                    activities_count: band.categories.reduce((total, category) => total + countActivities(category.items), 0),
                },
            },
        }));
    }, [ganttChart.data]);

    // A refetch that keeps the loaded window leaves the rows on screen while it loads. Emptying
    // them takes the grid's scrollable content away, and the browser clamps the scroll to 0.
    const rows = useMemo(() => {
        if (!ganttChart.data || (ganttChart.loading && !ganttChart.refreshing)) {
            return [];
        }

        if (ganttChart.filteredEmpty) {
            return [];
        }

        if (!bySource) {
            return getFlatRows(ganttChart.data.rows);
        }

        // A band is open unless it has been explicitly collapsed: by source is the default at
        // programme level, and opening onto a screen of closed bands would hide the plan
        // rather than organise it.
        return sourceBands.flatMap((band) => (expandedRows[band.row.data.key] !== false ? [band.row, ...getFlatRows(band.categories)] : [band.row]));
    }, [ganttChart.data, ganttChart.loading, ganttChart.refreshing, ganttChart.filteredEmpty, bySource, sourceBands, expandedRows, getFlatRows]);

    // Source only exists on the master plan; the other three follow the Columns panel. In the
    // by-source view the band already states the source for every row beneath it, so keeping
    // the column as well would repeat the same value down all 196px of it.
    const fixedColumns = useMemo(() => {
        const keys = ['name'];

        if (columns.owner) keys.push('owner');
        if (isMaster && columns.source && !bySource) keys.push('source');
        if (columns.people) keys.push('people');
        if (columns.cost) keys.push('cost');
        if (columns.float) keys.push('float');

        return keys;
    }, [columns, isMaster, bySource]);

    // The sort is cleared from the Float header, so it cannot outlive the column: drag would stay
    // locked with nothing on screen to unlock it.
    useEffect(() => {
        if (!columns.float && sortByFloat) {
            toggleSortByFloat();
        }
    }, [columns.float, sortByFloat, toggleSortByFloat]);

    const fixedColumnCount = fixedColumns.length;

    const fixedColumnsWidth = useMemo(() => fixedColumns.reduce((total, columnKey) => total + FIXED_COLUMN_WIDTHS[columnKey], 0), [fixedColumns]);

    // Recompute the virtualized grid whenever the visible row count changes (expanding/collapsing
    // rows, e.g. via Expand all/Collapse all) or a column is toggled. MultiGrid caches cell
    // sizes and positions internally, so without an explicit recompute newly revealed rows
    // (such as activity leaf rows under an expanded sub-category) are never rendered. (IB-748)
    // Sorting, the schedule and the switch restyle or reorder rows without changing how many there
    // are, so they force the redraw too.
    useEffect(() => {
        multiGridRef.current?.recomputeGridSize();
    }, [fixedColumns, headers, rows.length, schedule, criticalPath, sortByFloat]);

    const columnsCount = useMemo(() => columnsKeys?.length || 0, [columnsKeys]);

    // Which timeline column the server's anchor date falls in — an index into `columnsKeys`, i.e.
    // the scrollable grid's own column space, with the pinned columns excluded.
    const anchorColumnIndex = useMemo(() => {
        if (!anchorDate || typeof getColumnIndexForDate !== 'function') {
            return -1;
        }

        return getColumnIndexForDate(anchorDate);
    }, [anchorDate, getColumnIndexForDate]);

    const getPeriodWidth = useCallback(() => {
        if (!columnsCount) {
            return COLUMN_WIDTHS_MAP[period];
        }

        const width = (containerRef.current?.clientWidth - fixedColumnsWidth - 2) / columnsCount;

        // The container is unmeasured on the first render pass, which makes `width` NaN. Falling
        // back to the floor keeps every consumer on a real number — `estimatedColumnSize` in
        // particular, where a NaN would corrupt the grid's own total-size arithmetic rather than
        // just mis-sizing one column.
        if (!Number.isFinite(width)) {
            return COLUMN_WIDTHS_MAP[period];
        }

        return Math.max(width, COLUMN_WIDTHS_MAP[period]);
    }, [period, columnsCount, fixedColumnsWidth]);

    // Pinned columns take their own width; everything after them is a timeline period.
    const getColumnWidth = useCallback(
        ({ index }) => (index < fixedColumnCount ? FIXED_COLUMN_WIDTHS[fixedColumns[index]] : getPeriodWidth()),
        [fixedColumns, fixedColumnCount, getPeriodWidth],
    );

    // Dependency arrows. Nothing here runs while the Links switch is off, since `links` is null.
    const activityRowIndex = useMemo(
        () => (links?.links?.length && ganttChart.data?.rows ? buildActivityRowIndex(ganttChart.data.rows, sourceBandKeyOf) : null),
        [links, ganttChart.data],
    );

    // Data-row index by row key, in the scrollable grid's row space (header rows excluded).
    const rowIndexByKey = useMemo(() => (activityRowIndex ? new Map(rows.map((row, index) => [row.data.key, index])) : null), [activityRowIndex, rows]);

    // Arrows for a given column width, recomputed only when the width or an input changes. The
    // width is read when the grid renders, as the bars read it, because a resize re-renders the
    // grid without re-rendering this component.
    const arrowsForWidth = useMemo(() => {
        if (!activityRowIndex || !rowIndexByKey) {
            return null;
        }

        let cached = { width: null, arrows: null };

        return (columnWidth) => {
            if (cached.width !== columnWidth) {
                const scale = createTimelineScale({ period, columnsKeys, columnDateLookup, columnWidth });
                const computed = computeArrows({
                    links: links.links,
                    bars: links.bars,
                    activityIndex: activityRowIndex,
                    rowIndexByKey,
                    scale,
                    rowHeight: ROW_HEIGHT,
                    criticalIds: criticalActivityIds,
                });

                cached = { width: columnWidth, arrows: computed.length ? computed : null };
            }

            return cached.arrows;
        };
    }, [activityRowIndex, rowIndexByKey, links, period, columnsKeys, columnDateLookup, criticalActivityIds]);

    // Draws the arrows into the scrollable grid's own scrolling content, after its cells, so they
    // scroll with the bars. The other three grids MultiGrid renders get their cells only.
    const cellRangeRenderer = useCallback(
        (props) => {
            const children = defaultCellRangeRenderer(props);

            if (!arrowsForWidth || !props.parent.props.className?.includes(SCROLLABLE_GRID_CLASS)) {
                return children;
            }

            const columnWidth = getPeriodWidth();
            const arrows = arrowsForWidth(columnWidth);

            if (!arrows) {
                return children;
            }

            children.push(
                <DependencyArrows
                    key="dependency-arrows"
                    arrows={arrows}
                    rowStartIndex={props.rowStartIndex}
                    rowStopIndex={props.rowStopIndex}
                    width={columnsCount * columnWidth}
                    height={rows.length * ROW_HEIGHT}
                    left={props.horizontalOffsetAdjustment}
                    top={props.verticalOffsetAdjustment}
                />,
            );

            return children;
        },
        [arrowsForWidth, getPeriodWidth, columnsCount, rows.length],
    );

    // Read at fire time rather than depended on. `getPeriodWidth` closes over `fixedColumnsWidth`,
    // so it takes a new identity whenever a pinned column is toggled — as a dependency below that
    // would yank a user who had scrolled out to 2032 back to the first activity just for unticking
    // Cost. Dropping the dependency outright is not the answer either: the effect would then
    // compute the offset from a stale width and land in the wrong place.
    const getPeriodWidthRef = useRef(getPeriodWidth);
    getPeriodWidthRef.current = getPeriodWidth;

    // Which anchor has already been applied, as `period|columnsCount|index`. The effect below has
    // to re-enter on the commit that populates `rows` (see below), which means depending on
    // `rows.length` — and that alone would re-anchor on every expand/collapse. This key is what
    // keeps it one-shot: a re-entry for any other reason matches and exits.
    const appliedAnchorRef = useRef(null);

    // Land on the anchor date, so the Schedule tab opens on real work instead of on whatever the
    // timeline's first columns happen to be — the plan spans the EVENT's dates, which routinely
    // start long before anything is scheduled.
    //
    // Done imperatively rather than through either of MultiGrid's scroll props. `scrollToColumn` is
    // re-applied whenever `recomputeGridSize()` runs (which this component does on every row-count
    // change), so it would drag the grid back here every time a row expands. `scrollLeft` is worse,
    // not inert: MultiGrid's `getDerivedStateFromProps` copies it into state on every change, while
    // its own `onScroll` writes that same state — so once the user scrolls away the prop and the
    // state diverge, and the next re-render from any cause snaps them back to the stale prop. Do
    // not "simplify" this to either one.
    //
    // ⚠️ It must also wait for `rows`. While the tree is empty MultiGrid hands the scrollable grid
    // `rowCount: Math.max(0, rowCount - fixedRowCount)` — zero, since the only row is the fixed
    // header — and Grid renders no `innerScrollContainer` at all for zero rows. The node still
    // exists and still carries our class, so it passes a `querySelector` and looks scrollable, but
    // `scrollWidth === clientWidth` and any `scrollLeft` write is silently CLAMPED to 0. This is
    // deterministic ordering, not a race: React 16 does not batch across promise callbacks, so the
    // anchor date commits, and this effect runs, strictly before the tree populates `rows`.
    // Depending on `rows.length` is what lets it re-enter on the commit it can act on; the
    // scrollability check is what makes it skip the commits it cannot.
    //
    // Keyed on the resolved index, it fires once per anchor — and a filter or period change
    // resolves a new one, which is what makes the landing hold across both. `period` and
    // `columnsCount` are in the key as well because the same index can occur in two different
    // column spaces, and both of those change the space without necessarily moving the index.
    useEffect(() => {
        // Cleared rather than left stale: a period or filter change nulls the anchor date upstream,
        // and resetting here is what guarantees the next anchor is applied even when it resolves to
        // the column the previous one did — the "holds after filter changes" half.
        if (anchorColumnIndex < 0) {
            appliedAnchorRef.current = null;

            return;
        }

        const anchorKey = `${period}|${columnsCount}|${anchorColumnIndex}`;
        if (appliedAnchorRef.current === anchorKey) {
            return;
        }

        // ⚠️ Do NOT reach for `measureAllCells()` here to firm up the grid's width. It calls
        // `getSizeAndPositionOfCell(rowCount - 1)` (Grid.js:522), and while the tree is empty the
        // scrollable grid's rowCount is 0 — so it asks for index -1 and CellSizeAndPositionManager
        // throws ("Requested index -1 is outside of range 0..0"), taking the whole grid down on
        // every load. This effect necessarily runs in that empty-rows commit, so it is the one
        // place the call cannot be made. The `estimatedColumnSize` prop on the MultiGrid is what
        // keeps the width truthful instead, and it does so at every moment rather than just this
        // one — see the comment on that prop.
        const scrollGrid = containerRef.current?.querySelector(`.${SCROLLABLE_GRID_CLASS}`);
        if (!scrollGrid || scrollGrid.scrollWidth <= scrollGrid.clientWidth) {
            return;
        }

        const columnWidth = getPeriodWidthRef.current();
        if (!Number.isFinite(columnWidth) || columnWidth <= 0) {
            return;
        }

        // ⚠️ The key is recorded HERE, below every guard, and must stay below them. Hoisting it up
        // beside the comparison above — the obvious tidy-up, since that is where the key is built —
        // records an anchor that was never applied: the effect fires once with `rows` still empty,
        // bails on the scrollability check, and the re-entry that could actually scroll then
        // matches the key and exits. That silently reinstates the original bug.
        //
        // The scrollable grid measures scrollLeft from the first timeline column, so the index maps
        // straight onto it. Setting it on the node (rather than reaching into react-virtualized's
        // internals) leaves the library to react to a normal scroll event, which also re-runs the
        // lazy column load for wherever we landed.
        appliedAnchorRef.current = anchorKey;
        scrollGrid.scrollLeft = anchorColumnIndex * columnWidth;
    }, [anchorColumnIndex, period, columnsCount, rows.length]);

    // Handle drag end for reordering
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id || !over.data.current.acceptedTypes.includes(activeData.type)) {
            return;
        }

        const data = {
            originType: activeData.type,
            originId: activeData.id,
            destinationType: over.data.current.type,
            destinationId: over.data.current.id,
            originSource: activeData.source,
            destinationSource: over.data.current.source,
        };

        moveItems(data);

        setActiveData(null);
    };

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveData(active.data.current);
    };

    // Cell renderer with drag and drop functionality
    const cellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style }) => {
            const lastRowIndex = rows.length + headers.length - 1;
            const lastColumnIndex = fixedColumnCount + columnsCount - 1;
            // The wireframe's table is ruled horizontally only — its `td` carries a `border-top`
            // and nothing else, so no vertical line falls between Owner, People and the timeline.
            // The only verticals kept are the outer two, which are the card's own frame. The 20px
            // right border that used to sit on the last pinned column — a grey gutter standing in
            // for a scrollbar — goes with them; it appears nowhere in the design.
            const borderClassNames = classNames(GRID_LINE, {
                'tw-rounded-tl-lg tw-overflow-hidden': rowIndex === 0 && columnIndex === 0,
                'tw-rounded-tr-lg tw-overflow-hidden': rowIndex === 0 && columnIndex === lastColumnIndex,
                'tw-rounded-bl-lg tw-overflow-hidden': rowIndex === lastRowIndex && columnIndex === 0,
                'tw-rounded-br-lg tw-overflow-hidden': rowIndex === lastRowIndex && columnIndex === lastColumnIndex,
                'tw-border-t': rowIndex === 0,
                'tw-border-b': rowIndex === lastRowIndex,
                'tw-border-l': columnIndex === 0,
                'tw-border-r': columnIndex === lastColumnIndex,
            });

            // Pinned columns come first and in `fixedColumns` order; anything past them is a
            // timeline period.
            const fixedColumnKey = columnIndex < fixedColumnCount ? fixedColumns[columnIndex] : null;

            if (rowIndex < headers.length) {
                // Column headings: small, grey and left-aligned on the light band, so they read as
                // labels for the data rather than competing with it. People and Cost are the two
                // numeric columns and both align their values to the end, so their headings follow —
                // otherwise the heading sits at one edge of the column and the figure it labels at
                // the other, which reads as two unrelated things.
                if (fixedColumnKey === 'float') {
                    const isHeading = rowIndex === headers.length - 1;

                    return (
                        <div
                            key={key}
                            style={style}
                            role={isHeading ? 'columnheader' : undefined}
                            aria-sort={isHeading ? (sortByFloat ? 'ascending' : 'none') : undefined}
                            className={classNames(
                                'tw-flex tw-items-center tw-justify-end tw-px-1.5 tw-text-xs tw-font-semibold tw-text-charcoal-50',
                                HEADER_SURFACE,
                                borderClassNames,
                            )}
                        >
                            {isHeading && (
                                <button
                                    type="button"
                                    onClick={toggleSortByFloat}
                                    title={sortByFloat ? $t('Clear the Float sort') : $t('Sort by float, lowest first')}
                                    className={classNames('tw-inline-flex tw-items-center tw-gap-x-1', sortByFloat ? 'tw-text-blue' : 'hover:tw-text-charcoal')}
                                >
                                    {fixedColumnHeading(fixedColumnKey, bySource)}
                                    <SortIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    );
                }

                if (fixedColumnKey) {
                    return (
                        <div
                            key={key}
                            style={style}
                            className={classNames(
                                'tw-flex tw-items-center tw-px-1.5 tw-text-xs tw-font-semibold tw-text-charcoal-50',
                                HEADER_SURFACE,
                                { 'tw-justify-end': fixedColumnKey === 'people' || fixedColumnKey === 'cost' },
                                borderClassNames,
                            )}
                        >
                            {rowIndex === headers.length - 1 ? fixedColumnHeading(fixedColumnKey, bySource) : ''}
                        </div>
                    );
                }

                // The date header — one row in every period, each cell carrying its own full date.
                // It takes the same surface as the pinned headings beside it and carries no
                // vertical rules at all, so it reads as one unbroken band.
                const dateColumnIndex = columnIndex - fixedColumnCount;
                const headerData = headers[rowIndex].columns[dateColumnIndex];
                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames(
                            'tw-flex tw-items-center tw-px-1.5 tw-text-xs tw-font-semibold tw-text-charcoal-50',
                            borderClassNames,
                            HEADER_SURFACE,
                        )}
                    >
                        {headerData?.label}
                    </div>
                );
            }

            // Data rows with drag and drop functionality
            const dataRowIndex = rowIndex - headers.length;
            const row = rows[dataRowIndex];
            const data = row.data;

            const tone = criticalPath && data.type === ACTIVITY_TYPE ? activityTone(scheduleById.get(data.id)) : null;
            // Shown by a group only while it is collapsed.
            const showCriticalMarker = criticalPath && criticalGroups.has(data.key);

            // A source band spans the whole row. MultiGrid has no colspan, so every cell on the
            // row paints the band surface and only the first carries the label — which reads as
            // the single strip the design asks for, across the pinned columns and the timeline
            // alike.
            if (row.isBand) {
                const expanded = expandedRows[data.key] !== false;
                // The today marker is a single vertical rule down the whole timeline. Skipping it
                // on band rows would break it into segments at every source — and its arrowhead,
                // which sits on the first row, would never be drawn at all, because in this view
                // the first row is always a band.
                const bandColumnKey = fixedColumnKey ? null : columnsKeys[columnIndex - fixedColumnCount];

                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames('tw-flex tw-items-center tw-gap-x-1.5 tw-bg-[#eef1f5] tw-px-1.5 tw-border-t', borderClassNames)}
                    >
                        {bandColumnKey && line && line[bandColumnKey] && (
                            <VerticalLine offsetPercentage={line[bandColumnKey].offset} indicator={dataRowIndex === 0} />
                        )}
                        {columnIndex === 0 && (
                            <Fragment>
                                <button
                                    onClick={() => setRowExpanded(data.key, !expanded)}
                                    aria-expanded={expanded}
                                    title={expanded ? $t('Collapse source') : $t('Expand source')}
                                    aria-label={expanded ? $t('Collapse source') : $t('Expand source')}
                                    className="tw-shrink-0 tw-flex tw-items-center"
                                >
                                    {expanded ? (
                                        <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" aria-hidden="true" />
                                    ) : (
                                        <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" aria-hidden="true" />
                                    )}
                                </button>
                                <div className="tw-text-[12.5px] tw-font-semibold tw-text-[#1f2937] tw-truncate" title={data.description}>
                                    {data.description}
                                </div>
                                {!expanded && showCriticalMarker && <CriticalMarker />}
                                {/* The counts are what make a collapsed band worth collapsing — they
                                    say how much is folded away without reopening it. */}
                                <div className="tw-text-2xs tw-text-charcoal-50 tw-whitespace-nowrap">
                                    {$t('· {{categories}} categories · {{activities}} activities', {
                                        categories: data.categories_count,
                                        activities: data.activities_count,
                                    })}
                                </div>
                            </Fragment>
                        )}
                    </div>
                );
            }

            if (fixedColumnKey === 'name') {
                return (
                    <div key={key} style={style} className={classNames('tw-flex tw-items-center tw-border-t', borderClassNames)}>
                        {[CATEGORY_TYPE, SUBCATEGORY_TYPE].includes(data.type) ? (
                            <DraggableProjectCategoryCell
                                id={data.key}
                                categoryId={data.id}
                                type={data.type}
                                source={data.source_key}
                                activeType={activeData?.type}
                                activeSource={activeData?.source}
                                description={data.description}
                                level={data.level}
                                // Purely visual, and deliberately not folded into `level`: level 0
                                // is what marks a row as a category — it picks the darker surface
                                // and is the only level that offers "New subcategory".
                                indent={bySource ? 1 : 0}
                                plain={bySource}
                                deletable={deletable}
                                protectedFromDeletion={data.is_procurement}
                                disabledDeletion={disabledDeletionRows.has(data.key)}
                                checkedForDeletion={deletableRows.has(data.key)}
                                expandable={row.expandable}
                                expanded={expandedRows[data.key]}
                                criticalMarker={!expandedRows[data.key] && showCriticalMarker}
                                dragLocked={sortByFloat}
                                onToggleExpansionClick={() => toggleRow(data.key)}
                                onToggleDeletionClick={() => toggleDeletableRow(data.key)}
                                onCreateSubcategory={() => onSubcategoryCreate(data.id)}
                                onCreateActivity={() => onActivityCreate(data.id)}
                                onEdit={() => onCategoryEdit({ id: data.id, category_id: data.category_id, description: data.description })}
                            />
                        ) : (
                            <DraggableProjectActivityCell
                                id={data.key}
                                activityId={data.id}
                                type={data.type}
                                source={data.source_key}
                                activeType={activeData?.type}
                                activeSource={activeData?.source}
                                description={data.description}
                                detailedDescription={data.detailed_description}
                                targetCompletionDate={data.target_completion_date}
                                statusLabel={data.status_label}
                                earliestStartDate={data.earliest_start_date}
                                startConflictDays={data.start_conflict_days}
                                level={data.level}
                                indent={bySource ? 1 : 0}
                                tone={tone}
                                dragLocked={sortByFloat}
                                actions={data.actions_count}
                                deletable={deletable}
                                protectedFromDeletion={data.is_procurement}
                                disabledDeletion={disabledDeletionRows.has(data.key)}
                                checkedForDeletion={deletableRows.has(data.key)}
                                onToggleDeletionClick={() => toggleDeletableRow(data.key)}
                                onActionsClick={() => onActivityActionsShow(data)}
                                onEdit={() => onActivityEdit(data)}
                            />
                        )}
                    </div>
                );
            }

            if (fixedColumnKey === 'owner') {
                return (
                    <div key={key} style={style} className={classNames('tw-flex tw-items-center tw-px-1.5 tw-text-[13px] tw-border-t', borderClassNames)}>
                        {/* Keyed on the row rather than the grid position: MultiGrid reuses a cell
                            instance when rows shift (expand/collapse, filter, delete), and without a
                            remount the cell's owner-selection state would outlive the record it was
                            picked for — and get saved onto the row that took its place. */}
                        <OwnerCell key={data.key} rowId={data.id} type={data.type} name={data.owner_name} ownerId={data.owner_id} />
                    </div>
                );
            }

            if (fixedColumnKey === 'source') {
                return (
                    <div key={key} style={style} className={classNames('tw-flex tw-items-center tw-px-1.5 tw-text-[13px] tw-border-t', borderClassNames)}>
                        <div className="tw-truncate">{data.source_name}</div>
                    </div>
                );
            }

            if (fixedColumnKey === 'people') {
                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames('tw-flex tw-items-center tw-justify-end tw-px-1.5 tw-text-[13px] tw-border-t', borderClassNames)}
                    >
                        {data.type === ACTIVITY_TYPE && (
                            <Tooltip
                                tooltipType="content"
                                backgroundClass="tw-bg-charcoal-15"
                                tooltipContent={getTaggedPositionsTooltipContent(data.tagged_positions)}
                            >
                                {/* role="img" so the label is announced at all (aria-label is ignored on a generic
                                    div) and so the bare digit inside is not read out on its own. */}
                                <div
                                    className="tw-flex tw-items-center tw-gap-x-1 tw-text-charcoal-50"
                                    role="img"
                                    aria-label={$t('{{count}} people assigned', { count: data.tagged_positions_count })}
                                >
                                    <UsersIcon className="tw-h-3 tw-w-3 tw-shrink-0" aria-hidden="true" />
                                    <span className="tw-truncate inbud-numeric-text">{data.tagged_positions_count}</span>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            }

            if (fixedColumnKey === 'cost') {
                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames('tw-flex tw-items-center tw-justify-end tw-px-1.5 tw-text-[13px] tw-border-t', borderClassNames)}
                    >
                        {data.type === ACTIVITY_TYPE && <div className="tw-truncate inbud-numeric-text">{formatToFixedMoneyWithLocale(data.cost)}</div>}
                    </div>
                );
            }

            if (fixedColumnKey === 'float') {
                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames('tw-flex tw-items-center tw-justify-end tw-px-1.5 tw-text-[13px] tw-border-t', borderClassNames)}
                    >
                        {data.type === ACTIVITY_TYPE && schedule && (
                            <FloatValue
                                entry={scheduleById.get(data.id)}
                                hasTarget={!!schedule.target_id}
                                nearCriticalMax={schedule.near_critical_max}
                                coloured={criticalPath}
                            />
                        )}
                    </div>
                );
            }

            const columnKey = columnsKeys[columnIndex - fixedColumnCount];

            // Bar/completion geometry comes from the lazily-loaded column overlay
            // (keyed by activity key then column key), not the row tree — columns
            // outside the loaded window render empty until scrolled into view.
            let bar = null;
            let completionLine = null;
            const activityColumns = columnsByKey[data.key];
            if (activityColumns && activityColumns[columnKey]) {
                bar = activityColumns[columnKey].bar;
                completionLine = activityColumns[columnKey].completion_line;
            }

            return (
                <div key={key} style={style} className={classNames('tw-flex tw-items-center tw-text-[13px] tw-border-t', borderClassNames)}>
                    {bar && (
                        <Bar
                            offsetPercentage={bar.offset_percentage}
                            lengthPercentage={bar.length_percentage}
                            isClosedStart={bar.is_closed_start}
                            isClosedEnd={bar.is_closed_end}
                            percentage={bar.percentage}
                            completeness={bar.completeness}
                            status={bar.status}
                            periodWidth={getPeriodWidth()}
                            isKeyMilestone={bar.type === 'key-milestone'}
                            tone={tone}
                        />
                    )}
                    {line && line[columnKey] && <VerticalLine offsetPercentage={line[columnKey].offset} indicator={dataRowIndex === 0} />}
                    {completionLine && (
                        <CompletionLine
                            offsetPercentage={completionLine.offset_percentage}
                            lengthPercentage={completionLine.length_percentage}
                            isClosedEnd={completionLine.is_closed_end}
                            periodWidth={getPeriodWidth()}
                            dimmed={tone === TONE_DIMMED}
                        />
                    )}
                </div>
            );
        },
        [
            rows,
            headers,
            expandedRows,
            deletable,
            deletableRows,
            disabledDeletionRows,
            fixedColumns,
            fixedColumnCount,
            columnsCount,
            columnsKeys,
            activeData,
            getPeriodWidth,
            columnsByKey,
            line,
            onCategoryEdit,
            onSubcategoryCreate,
            onActivityActionsShow,
            onActivityEdit,
            toggleRow,
            setRowExpanded,
            toggleDeletableRow,
            bySource,
            schedule,
            scheduleById,
            criticalPath,
            criticalGroups,
            sortByFloat,
            toggleSortByFloat,
        ],
    );

    // On horizontal scroll, translate the visible column-index range (+buffer)
    // into a date range and ask the context to lazily load any not-yet-loaded
    // columns. Debounced so a scroll gesture fires at most one request burst.
    const handleGridScroll = useCallback(
        ({ scrollLeft }) => {
            if (!columnsCount || typeof getDateRangeForColumnIndexes !== 'function' || typeof loadColumnWindow !== 'function') {
                return;
            }

            if (scrollDebounceRef.current) {
                clearTimeout(scrollDebounceRef.current);
            }

            scrollDebounceRef.current = setTimeout(() => {
                const periodWidth = getPeriodWidth();
                if (!periodWidth) {
                    return;
                }

                // scrollLeft is measured from the first scrollable (data) column,
                // so it maps directly onto the columnsKeys index space.
                const viewportWidth = Math.max((containerRef.current?.clientWidth || 0) - fixedColumnsWidth, periodWidth);
                const firstVisible = Math.floor(scrollLeft / periodWidth);
                const lastVisible = firstVisible + Math.ceil(viewportWidth / periodWidth);

                // Landing band = visible range ± buffer, HARD-CAPPED to
                // MAX_FETCH_COLUMNS columns and centred on the viewport. This is
                // what bounds each fetch to the window width regardless of how
                // far the scrollbar jumped (AC#2); loadColumnWindow then either
                // edge-extends or re-anchors to this band.
                let bandStart = firstVisible - SCROLL_BUFFER_COLUMNS;
                let bandEnd = lastVisible + SCROLL_BUFFER_COLUMNS;
                if (bandEnd - bandStart + 1 > MAX_FETCH_COLUMNS) {
                    const centre = Math.floor((firstVisible + lastVisible) / 2);
                    bandStart = centre - Math.floor(MAX_FETCH_COLUMNS / 2);
                    bandEnd = bandStart + MAX_FETCH_COLUMNS - 1;
                }

                const range = getDateRangeForColumnIndexes(bandStart, bandEnd);
                if (range) {
                    loadColumnWindow(range.from, range.to);
                }
            }, 150);
        },
        [columnsCount, fixedColumnsWidth, getPeriodWidth, getDateRangeForColumnIndexes, loadColumnWindow],
    );

    useEffect(() => {
        return () => {
            if (scrollDebounceRef.current) {
                clearTimeout(scrollDebounceRef.current);
            }
        };
    }, []);

    return (
        <Fragment>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                {/* `tw-relative` is load-bearing: the skeleton, the progress ring and the empty
                    state all fill this frame with `tw-absolute tw-inset-0`, and without a
                    positioned ancestor here they resolved against whichever ancestor happened to
                    be positioned — which is why the old "No data found" sat in the middle of the
                    page rather than in the middle of the grid.
                    The border is `tw-border` as well as `tw-border-grey`: the colour was set
                    without a width, so no border had ever actually drawn. With the width and the
                    radius the grid reads as the wireframe's card instead of running off the edge
                    of the page. */}
                <div
                    ref={containerRef}
                    className={classNames(
                        'tw-relative tw-min-h-0 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-grey',
                        criticalPath ? 'tw-mb-0' : 'tw-mb-10',
                    )}
                >
                    {/* A resize changes the stretched period width, which the grid has cached
                        per column, so the cached sizes are dropped with it. */}
                    <AutoSizer onResize={() => multiGridRef.current?.recomputeGridSize()}>
                        {({ width, height }) => (
                            <MultiGrid
                                ref={multiGridRef}
                                width={width - 2}
                                height={height - 4}
                                rowHeight={ROW_HEIGHT}
                                rowCount={rows.length + headers.length}
                                columnWidth={getColumnWidth}
                                /**
                                 * ⚠️ Load-bearing, not a performance hint. `columnWidth` is a
                                 * function, so Grid sizes every not-yet-measured column at
                                 * `estimatedColumnSize` — which defaults to 100px, nothing like any
                                 * of our widths. It measures columns only as it renders them, so
                                 * `getTotalSize()` reads as (measured) + (unmeasured x 100) and
                                 * comes out far short of the real width. `handleScrollEvent` then
                                 * re-clamps every observed scroll offset to that short total and
                                 * writes it back, so a scroll past it is silently pulled backwards.
                                 *
                                 * That is invisible until something depends on an exact offset. It
                                 * put the Quarters view one column short of its anchor: 12 columns
                                 * at 300px with 5 measured estimates the total at 2200 instead of
                                 * 3600, capping the offset at 814 when the anchor needed 900. Days
                                 * and Months escaped it only because 128px is near enough to 100px
                                 * that their totals stayed ahead of the offsets being asked for.
                                 *
                                 * Every timeline column is exactly `getPeriodWidth()` wide, so
                                 * passing it makes the estimate exact and the total correct however
                                 * few columns have been measured. It also fixes the grid's own
                                 * scrollWidth, which was understating how far a USER could scroll.
                                 */
                                estimatedColumnSize={getPeriodWidth()}
                                columnCount={fixedColumnCount + columnsCount}
                                fixedColumnCount={fixedColumnCount}
                                enableFixedColumnScroll
                                fixedRowCount={headers.length}
                                cellRenderer={cellRenderer}
                                cellRangeRenderer={cellRangeRenderer}
                                onScroll={handleGridScroll}
                                classNameBottomRightGrid={SCROLLABLE_GRID_CLASS}
                                /**
                                 * Pass-thru props - This is due to the need of force re-render (https://github.com/bvaughn/react-virtualized?tab=readme-ov-file#pass-thru-props)
                                 */
                                rows={rows}
                                headers={headers}
                                period={period}
                                fixedColumns={fixedColumns}
                                expandedRows={expandedRows}
                                activeData={activeData}
                                deletableRows={deletableRows}
                                deletable={deletable}
                                columnsByKey={columnsByKey}
                                line={line}
                                arrowsForWidth={arrowsForWidth}
                                schedule={schedule}
                                criticalPath={criticalPath}
                                criticalActivityIds={criticalActivityIds}
                                sortByFloat={sortByFloat}
                            />
                        )}
                    </AutoSizer>

                    {/* Both branches test `ganttChart.data`, not just `loading`. Between mount and
                        the first request actually being in flight, `data` is still null while
                        `loading` is not yet true — and without the data check that window fell
                        through to the empty state, so it flashed on every single load before the
                        plan appeared. */}
                    {!rows.length && (ganttChart.loading || !ganttChart.data) && (
                        <>
                            <GanttChartSkeleton fixedColumns={fixedColumns} fixedColumnWidths={FIXED_COLUMN_WIDTHS} rowHeight={ROW_HEIGHT} />
                            {/* Kept over the skeleton rather than replaced by it: the percentage is
                                real download progress, and on a plan of several thousand activities
                                it is the only sign that a long wait is actually moving. */}
                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                                <CircularProgress percentage={ganttChart.progress} size="lg" />
                            </div>
                        </>
                    )}

                    {!rows.length && !ganttChart.loading && ganttChart.data && (
                        <ProjectPlanEmptyState
                            variant={emptyVariant}
                            searchTerm={ganttChart.data?.requested?.search || ''}
                            canManage={canManage}
                            onAddCategory={onCategoryCreate}
                            importUrl={importProjectPlanUrl}
                            onClearFilters={clearFilters}
                            onClearSearch={clearSearch}
                        />
                    )}
                </div>
                <DndKitDragOverlay>
                    <DragOverlay data={activeData?.overlay} />
                </DndKitDragOverlay>
            </DndContext>
            {/* The grid's bottom margin moves below the legend while it shows. */}
            {criticalPath && (
                <div className="!tw-mt-3 tw-mb-10 tw-flex tw-shrink-0 tw-items-center tw-gap-x-1.5 tw-text-xs tw-text-charcoal-75">
                    <span className="tw-inline-block tw-h-2.5 tw-w-3.5 tw-rounded-sm tw-border tw-border-red tw-bg-red-25" aria-hidden="true" />
                    {$t('On critical path')}
                </div>
            )}
        </Fragment>
    );
};

/** Marks a collapsed group that holds critical work. */
const CriticalMarker = () => (
    <span
        role="img"
        title={$t('Contains critical activities')}
        aria-label={$t('Contains critical activities')}
        className="tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-red"
    />
);

const MutedFloatText = ({ text }) => (
    <span className="tw-truncate tw-text-charcoal-50" title={text}>
        {text}
    </span>
);

/**
 * An activity's float in days, or why it has none. Coloured only while the Critical path switch is
 * on: 0 red, near-critical amber.
 */
const FloatValue = ({ entry, hasTarget, nearCriticalMax, coloured }) => {
    const label = floatLabel(entry, hasTarget);

    if (!label) {
        return null;
    }

    if (label.ariaLabel) {
        return (
            <span className="tw-text-charcoal-50" role="img" aria-label={label.ariaLabel}>
                {label.text}
            </span>
        );
    }

    if (label.muted) {
        return <MutedFloatText text={label.text} />;
    }

    return (
        <span
            className={classNames('inbud-numeric-text', {
                'tw-font-medium tw-text-red': coloured && entry.float === 0,
                'tw-font-medium tw-text-amber-700': coloured && isNearCriticalFloat(entry.float, nearCriticalMax),
            })}
        >
            {label.text}
        </span>
    );
};

const DragOverlay = ({ data }) => {
    if (!data) {
        return null;
    }

    return (
        <div
            className={classNames(
                'tw-w-full tw-h-full tw-flex tw-items-center tw-gap-x-1.5 tw-px-1.5 tw-text-[13px] tw-border-grey tw-border tw-bg-white tw-opacity-90',
                {
                    [CATEGORY_SURFACE]: data.type === CATEGORY_TYPE,
                    [SUBCATEGORY_SURFACE]: data.type === SUBCATEGORY_TYPE,
                    'tw-bg-white': data.type === ACTIVITY_TYPE,
                },
            )}
        >
            <GripIcon className="tw-w-4 tw-h-4" />
            <div className="tw-min-w-0 tw-flex tw-items-center tw-gap-x-1.5" style={{ paddingLeft: data.level * 18 }}>
                <div className="tw-truncate">{data.description}</div>
            </div>
        </div>
    );
};

/**
 * The wireframe's ⓘ beside an activity name: a read-only preview of the activity's detailed
 * description, due date and status, so a row can be understood without opening the editor.
 *
 * Hover opens the panel; clicking the icon pins it so a long description can be scrolled, and
 * Escape or a click elsewhere unpins. The pin is held here rather than handed to the tooltip
 * library because the library reports hover-out and outside-click through the same callback, and
 * only the second of the two should close a pinned panel.
 */
const ActivityInfoTooltip = ({ description, detailedDescription, targetCompletionDate, statusLabel }) => {
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!pinned) {
            return undefined;
        }

        const unpinOnOutsideClick = (event) => {
            if (triggerRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) {
                return;
            }
            setPinned(false);
        };
        const unpinOnEscape = (event) => {
            if (event.key === 'Escape') {
                setPinned(false);
            }
        };

        document.addEventListener('mousedown', unpinOnOutsideClick);
        document.addEventListener('keydown', unpinOnEscape);

        return () => {
            document.removeEventListener('mousedown', unpinOnOutsideClick);
            document.removeEventListener('keydown', unpinOnEscape);
        };
    }, [pinned]);

    // Nothing to preview — no affordance. The server sends a due date and a status on every
    // activity, so in practice this only guards rows the payload has yet to catch up with.
    if (!detailedDescription && !targetCompletionDate && !statusLabel) {
        return null;
    }

    const tooltipContent = (
        // Only the description scrolls. Capping the whole panel instead would push the due date and
        // status — the two things worth glancing at — below the fold on a long description, which
        // is exactly the row you most want them on.
        <div ref={panelRef} className="tw-text-xs tw-space-y-1 tw-max-w-[290px]">
            <div className="tw-font-semibold tw-text-white">{description}</div>
            {detailedDescription && (
                <div className="tw-text-white tw-opacity-80 tw-whitespace-pre-line tw-max-h-40 tw-overflow-y-auto">{detailedDescription}</div>
            )}
            {targetCompletionDate && statusLabel && (
                <div className="tw-text-white tw-opacity-80">
                    {$t('Due {{date}} · {{status}}', { date: formatDateLong(targetCompletionDate), status: statusLabel })}
                </div>
            )}
        </div>
    );

    return (
        <span ref={triggerRef} className="tw-inline-flex tw-shrink-0">
            <Tooltip
                tooltipType="content"
                backgroundClass="tw-bg-charcoal"
                // Beside the icon as in the wireframe; popper still flips it when the row is too
                // close to an edge for it to fit there.
                placement="right"
                trigger={['hover', 'focus']}
                visible={pinned || hovered}
                onVisibleChange={setHovered}
                closeOnOutsideClick={false}
                tooltipContent={tooltipContent}
            >
                <button
                    type="button"
                    className="tw-flex tw-items-center tw-text-blue"
                    aria-label={$t('Activity details')}
                    aria-expanded={pinned}
                    onClick={(event) => {
                        // The row carries handlers of its own; pinning the preview is not one of
                        // them, and must not reach them.
                        event.stopPropagation();
                        setPinned((previouslyPinned) => !previouslyPinned);
                    }}
                >
                    <CircleInfoIcon className="tw-h-3.5 tw-w-3.5 tw-shrink-0" aria-hidden="true" />
                </button>
            </Tooltip>
        </span>
    );
};

/**
 * The wireframe's row-level `＋ Add ▾`: a small blue link beside a category or sub-category name
 * that opens a panel naming what you are adding to, with one chip per thing you can add. A
 * category takes a sub-category or an activity; a sub-category takes only an activity.
 *
 * Drawn as a floating panel rather than the extra table row the wireframe uses, because the grid
 * is virtualised at a fixed row height — an inline row would have to be synthesised into the row
 * model and would shift every row beneath it on open.
 */
const RowAddMenu = ({ type, description, onCreateSubcategory, onCreateActivity }) => {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    const isCategory = type === CATEGORY_TYPE;

    const act = (handler) => {
        close();
        handler();
    };

    return (
        <span ref={triggerRef} className="tw-inline-flex">
            <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                className="tw-ms-2.5 tw-shrink-0 tw-whitespace-nowrap tw-text-[11px] tw-font-medium tw-text-blue"
            >
                ＋ {$t('Add')} ▾
            </button>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        role="menu"
                        className="tw-inline-block tw-rounded-lg tw-border tw-border-blue tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-text-charcoal tw-shadow-primary"
                    >
                        <span>
                            {isCategory ? $t('Add to category') : $t('Add to sub-category')} <b>{description}</b>:
                        </span>
                        {isCategory && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => act(onCreateSubcategory)}
                                className="tw-ms-2 tw-rounded-md tw-bg-[#E8F0FE] tw-px-2.5 tw-py-[3px] tw-text-blue"
                            >
                                ＋ {$t('Sub-category')}
                            </button>
                        )}
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => act(onCreateActivity)}
                            className="tw-ms-2 tw-rounded-md tw-bg-[#E8F0FE] tw-px-2.5 tw-py-[3px] tw-text-blue"
                        >
                            ＋ {$t('Activity')}
                        </button>
                    </div>,
                    document.body,
                )}
        </span>
    );
};

const DraggableProjectCategoryCell = (props) => {
    const { isMaster } = useContext(ProjectPlanContext);
    const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
        id: props.id,
        disabled: (props.type === SUBCATEGORY_TYPE && props.activeType === CATEGORY_TYPE) || props.activeSource !== props.source,
        data: {
            id: props.categoryId,
            type: props.type,
            source: props.source,
            acceptedTypes: props.type === SUBCATEGORY_TYPE ? [SUBCATEGORY_TYPE, ACTIVITY_TYPE] : [CATEGORY_TYPE, SUBCATEGORY_TYPE, ACTIVITY_TYPE],
            acceptedSource: props.source,
        },
    });
    const {
        attributes,
        listeners,
        setNodeRef: setDraggableNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: props.id,
        disabled: props.dragLocked || (props.deletable || isMaster ? props.source != 'master' : false),
        data: {
            id: props.categoryId,
            type: props.type,
            source: props.source,
            overlay: {
                level: props.level,
                type: props.type,
                source: props.source,
                description: props.description,
            },
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        zIndex: isDragging ? 1000 : 'auto',
        visibility: isDragging ? 'hidden' : 'visible',
    };

    return (
        <div ref={setDroppableNodeRef} className={classNames('tw-w-full tw-h-full', { 'tw-bg-blue-50': isOver })}>
            {!isOver ? (
                <div
                    ref={setDraggableNodeRef}
                    style={style}
                    className={classNames(
                        'tw-w-full tw-h-full tw-flex tw-items-center tw-gap-x-1.5 tw-px-1.5 tw-text-[13px]',
                        // The wireframe steps weight down the tree — 600 on a category, 500 on a
                        // sub-category, 300 on an activity — so depth reads from the type itself
                        // rather than only from the indent.
                        //
                        // Except at the top of the by-source view, where the wireframe draws
                        // categories as plain rows (`.prow`, not `.lvl-cat`): the band above them
                        // already carries the shaded tone, and repeating it on the row beneath
                        // merges the two into one grey mass. They keep a medium weight so they
                        // still read as a tier above their activities.
                        props.level > 0
                            ? `${SUBCATEGORY_SURFACE} tw-font-medium`
                            : props.plain
                              ? 'tw-bg-white tw-font-medium'
                              : `${CATEGORY_SURFACE} tw-font-semibold`,
                    )}
                >
                    <DragHandle attributes={attributes} listeners={listeners} isDragging={isDragging} locked={props.dragLocked}>
                        {props.deletable ? (
                            <CheckboxInput
                                checked={!props.protectedFromDeletion && (props.disabledDeletion || props.checkedForDeletion)}
                                disabled={props.protectedFromDeletion || props.disabledDeletion}
                                onChange={(e) => props.onToggleDeletionClick()}
                            />
                        ) : (
                            <GripIcon className="tw-w-4 tw-h-4" />
                        )}
                    </DragHandle>
                    <div className="tw-min-w-0 tw-flex tw-items-center tw-gap-x-1.5" style={{ paddingLeft: (props.level + (props.indent || 0)) * 18 }}>
                        <div className="tw-w-3 tw-flex tw-items-center tw-shrink-0">
                            {props.expandable && (
                                <button onClick={() => props.onToggleExpansionClick()}>
                                    {props.expanded ? (
                                        <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                    ) : (
                                        <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="tw-truncate" title={props.description}>
                            {props.description}
                        </div>
                        {props.criticalMarker && <CriticalMarker />}
                        {/* Beside the name, as in the wireframe — adding belongs to the row it
                            names, not to a strip of icons at the far edge of the column. */}
                        <RowAddMenu
                            type={props.type}
                            description={props.description}
                            onCreateSubcategory={props.onCreateSubcategory}
                            onCreateActivity={props.onCreateActivity}
                        />
                    </div>
                    {/* The toolbar no longer carries a legend for these icons, so each one names
                        itself on hover and to a screen reader. */}
                    <div className="tw-ms-auto tw-flex tw-items-center tw-gap-x-1">
                        <button
                            onClick={() => props.onEdit()}
                            title={$t('Edit category, subcategory, or activity')}
                            aria-label={$t('Edit category, subcategory, or activity')}
                        >
                            <PenSimpleIcon className="tw-w-4 tw-h-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="tw-w-full tw-h-full"></div>
            )}
        </div>
    );
};

/** The grip or delete checkbox a row is dragged by. Locked while the rows are sorted by float. */
const DragHandle = ({ attributes, listeners, isDragging, locked, children }) => (
    <div
        {...attributes}
        {...listeners}
        title={locked ? $t('Clear the Float sort to reorder') : undefined}
        style={{ cursor: locked ? 'default' : isDragging ? 'grabbing' : 'grab' }}
    >
        {children}
    </div>
);

/** Flags an activity that starts before its predecessors allow. */
const StartConflictWarning = ({ days, earliestStartDate }) => {
    const message = startConflictMessage(days, earliestStartDate);

    return (
        <span className="tw-inline-flex tw-shrink-0">
            <Tooltip
                tooltipType="content"
                backgroundClass="tw-bg-charcoal"
                placement="right"
                trigger={['hover', 'focus']}
                tooltipContent={<div className="tw-text-xs tw-text-white tw-max-w-[290px]">{message}</div>}
            >
                <span role="img" tabIndex={0} className="tw-flex tw-items-center tw-text-amber-500" aria-label={message}>
                    <WarningTriangleIcon className="tw-h-3.5 tw-w-3.5 tw-shrink-0" aria-hidden="true" />
                </span>
            </Tooltip>
        </span>
    );
};

const DraggableProjectActivityCell = (props) => {
    const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
        id: props.id,
        disabled: props.activeType !== ACTIVITY_TYPE || props.activeSource !== props.source,
        data: { id: props.activityId, type: props.type, source: props.source, acceptedTypes: [ACTIVITY_TYPE], acceptedSource: props.source },
    });
    const {
        attributes,
        listeners,
        setNodeRef: setDraggableNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: props.id,
        disabled: props.dragLocked || props.deletable,
        data: {
            id: props.activityId,
            type: props.type,
            source: props.source,
            overlay: {
                level: props.level,
                type: props.type,
                source: props.source,
                description: props.description,
            },
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        zIndex: isDragging ? 1000 : 'auto',
        visibility: isDragging ? 'hidden' : 'visible',
    };

    return (
        <div ref={setDroppableNodeRef} className={classNames('tw-w-full tw-h-full', { 'tw-bg-blue-50': isOver })}>
            {!isOver ? (
                <div
                    ref={setDraggableNodeRef}
                    style={style}
                    className="tw-w-full tw-h-full tw-flex tw-items-center tw-gap-x-1.5 tw-px-1.5 tw-text-[13px] tw-font-light tw-bg-white"
                >
                    <DragHandle attributes={attributes} listeners={listeners} isDragging={isDragging} locked={props.dragLocked}>
                        {props.deletable ? (
                            <CheckboxInput
                                checked={!props.protectedFromDeletion && (props.disabledDeletion || props.checkedForDeletion)}
                                disabled={props.protectedFromDeletion || props.disabledDeletion}
                                onChange={(e) => props.onToggleDeletionClick()}
                            />
                        ) : (
                            <GripIcon className="tw-w-4 tw-h-4" />
                        )}
                    </DragHandle>
                    <div className="tw-min-w-0 tw-flex tw-items-center tw-gap-x-1.5" style={{ paddingLeft: (props.level + (props.indent || 0)) * 18 }}>
                        {/* Before the name, as in the wireframe — the preview belongs to the
                            activity it sits in front of. */}
                        <ActivityInfoTooltip
                            description={props.description}
                            detailedDescription={props.detailedDescription}
                            targetCompletionDate={props.targetCompletionDate}
                            statusLabel={props.statusLabel}
                        />
                        {!!props.startConflictDays && <StartConflictWarning days={props.startConflictDays} earliestStartDate={props.earliestStartDate} />}
                        <div className={classNames('tw-truncate', { 'tw-text-charcoal-50': props.tone === TONE_DIMMED })} title={props.description}>
                            {props.description}
                        </div>
                    </div>
                    <div className="tw-ms-auto tw-flex tw-items-center tw-gap-x-1">
                        <button
                            className="tw-h-3.5 tw-w-3.5 tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-bg-charcoal-50 tw-text-white tw-text-3xs"
                            onClick={props.onActionsClick}
                            title={$t('Actions')}
                            aria-label={$t('Actions')}
                        >
                            {props.actions}
                        </button>
                        <button
                            onClick={props.onEdit}
                            title={$t('Edit category, subcategory, or activity')}
                            aria-label={$t('Edit category, subcategory, or activity')}
                        >
                            <PenSimpleIcon className="tw-w-4 tw-h-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="tw-w-full tw-h-full"></div>
            )}
        </div>
    );
};

/** `tone` turns a critical bar and its diamond red, and fades a dimmed one. */
const Bar = ({
    offsetPercentage,
    lengthPercentage,
    percentage,
    completeness,
    status,
    isClosedStart,
    isClosedEnd,
    periodWidth,
    isKeyMilestone,
    tone = null,
}) => {
    periodWidth -= isClosedEnd ? 2 : 0; // Reduce border width (2px) if has closed end

    let left = (periodWidth * offsetPercentage) / 100;
    let width = (periodWidth * lengthPercentage) / 100;

    const critical = tone === TONE_CRITICAL;
    const dimmed = tone === TONE_DIMMED;

    return (
        <div className="tw-absolute tw-z-20 tw-top-0 tw-bottom-0 tw-my-auto tw-text-2xs" style={{ left, width, height: 18 }}>
            {/* A faded bar is see-through, so a solid backing keeps the completion line beneath it hidden. */}
            {dimmed && <div className={classNames('tw-absolute tw-inset-0 tw-bg-white', { 'tw-rounded-l': isClosedStart, 'tw-rounded-r': isClosedEnd })} />}
            {/* The diamond used to be explained by a legend above the table. Now that the legend
                is gone it names itself: a title for the mouse, an aria-label for a screen reader. */}
            {isClosedEnd && isKeyMilestone && (
                <span
                    className={classNames('tw-absolute tw-z-10 -tw-right-[5px] tw-top-0 tw-bottom-0 tw-my-auto tw-flex tw-items-center', {
                        'tw-opacity-45': dimmed,
                    })}
                    title={$t('Key Milestone')}
                    role="img"
                    aria-label={$t('Key Milestone')}
                >
                    <DiamondIcon className={classNames('tw-w-3 tw-h-3', critical ? 'tw-fill-red tw-text-red' : 'tw-fill-yellow-300')} aria-hidden="true" />
                </span>
            )}
            <div
                className={classNames('tw-absolute tw-inset-0 tw-overflow-hidden tw-border-t tw-border-b', {
                    'tw-border-[#223322]': !critical,
                    'tw-border-red tw-bg-red-25': critical,
                    'tw-bg-blue-75 tw-text-white': !critical && status === 'completed',
                    'tw-bg-background-4': !critical && status === 'overdue',
                    'tw-bg-background-3': !critical && status === 'in_progress',
                    'tw-border-l tw-rounded-l': isClosedStart,
                    'tw-border-r tw-rounded-r': isClosedEnd,
                    'tw-opacity-45': dimmed,
                })}
            >
                {percentage > 0 && ['in_progress', 'overdue'].includes(status) && (
                    <div
                        className={classNames('tw-absolute tw-inset-0', {
                            'tw-bg-red-50': critical,
                            'tw-bg-background-6': !critical && status === 'overdue',
                            'tw-bg-background-5': !critical && status === 'in_progress',
                        })}
                        style={{ width: `${percentage}%` }}
                    />
                )}
                {isClosedEnd && (
                    <div className="tw-absolute tw-right-2 tw-top-0 tw-bottom-0 tw-flex tw-items-center tw-text-[8px] tw-font-semibold">{completeness}%</div>
                )}
            </div>
        </div>
    );
};

/** `dimmed` fades the line with its activity's bar. */
const CompletionLine = ({ offsetPercentage, lengthPercentage, isClosedEnd, periodWidth, dimmed = false }) => {
    periodWidth -= isClosedEnd ? 2 : 0; // Reduce border width (2px) if has closed end

    let left = (periodWidth * offsetPercentage) / 100;
    let width = (periodWidth * lengthPercentage) / 100;

    return (
        <div
            className={classNames('tw-absolute tw-inset-0 tw-z-10 tw-h-0 tw-my-auto tw-border-t tw-border-t-charcoal tw-border-dashed', {
                'tw-opacity-45': dimmed,
            })}
            style={{ left, width }}
        ></div>
    );
};

const VerticalLine = ({ offsetPercentage, indicator }) => {
    return (
        <div
            className="tw-w-0.5 tw-flex tw-justify-center tw-absolute tw-z-10 tw-top-0 tw-bottom-0 tw-bg-charcoal-50"
            style={{ left: `${offsetPercentage}%`, height: 'calc(100% + 2px)' }}
        >
            {indicator && <TriangleDownIcon className="tw-min-w-3 tw-w-3 tw-text-charcoal-50" />}
        </div>
    );
};

export default DraggableGanttChart;
